import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GeneralResponse } from '../../data-models/shared.data-models';
import { BaseApiService } from 'src/app/shared/services/base-api.service';

export interface PresignUploadResponse {
  storedFileId: string;
  uploadUrl: string;
  httpMethod: string;
  headers: Record<string, string>;
  expiresAtUtc: string;
}

export interface PresignDownloadResponse {
  storedFileId: string;
  url: string;
  expiresAtUtc: string;
  mimeType?: string;
  fileName?: string;
}

@Injectable({ providedIn: 'root' })
export class AttachmentService {
  private readonly baseUrl = `${environment.appConfig.gatewayServiceBaseUrl}/api/1/Attachments`;

  constructor(
    private baseApi: BaseApiService<GeneralResponse<any>>,
    private translate: TranslateService
  ) {}

  async uploadFile(file: File, purpose: number | string): Promise<{ storedFileId: string }> {
    const contentType = file.type || 'application/octet-stream';
    const fileExtension = this.extensionFromFile(file);
    let presignRes: GeneralResponse<any>;
    try {
      presignRes = await firstValueFrom(
        this.baseApi.Post(`${this.baseUrl}/presign-upload`, {
          purpose,
          contentType,
          fileExtension,
          originalFileName: file.name,
          sizeInBytes: file.size,
        })
      );
    } catch (err) {
      throw this.friendlyError(err, 'Attachments.UPLOAD_FAILED');
    }
    const presign = presignRes?.resource;
    if (!presign?.storedFileId || !presign.uploadUrl) {
      throw new Error(presignRes?.message || this.translate.instant('Attachments.UPLOAD_FAILED'));
    }

    // Direct upload to storage: its errors carry no API message, so show the generic text (no status/URL).
    let put: Response;
    try {
      put = await fetch(presign.uploadUrl, {
        method: presign.httpMethod || 'PUT',
        headers: { ...(presign.headers || {}) },
        body: file,
      });
    } catch {
      throw new Error(this.translate.instant('Attachments.UPLOAD_FAILED'));
    }
    if (!put.ok) {
      throw new Error(this.translate.instant('Attachments.UPLOAD_FAILED'));
    }
    return { storedFileId: presign.storedFileId };
  }

  async getViewUrl(storedFileId: string, download = false): Promise<string> {
    let res: GeneralResponse<any>;
    try {
      res = await firstValueFrom(
        this.baseApi.Get(
          `${this.baseUrl}/${encodeURIComponent(storedFileId)}/url?download=${download}`
        )
      );
    } catch (err) {
      throw this.friendlyError(err, 'Attachments.FILE_NOT_AVAILABLE');
    }
    const url = res?.resource?.url;
    if (!url) {
      throw new Error(res?.message || this.translate.instant('Attachments.FILE_NOT_AVAILABLE'));
    }
    return url;
  }

  /**
   * Turns an HttpClient error into an Error whose message can be shown to the user: the API's own
   * message when it sent one (e.g. "This file isn't available right now."), otherwise a translated
   * fallback — never Angular's "Http failure response for <url>: 404 Not Found".
   */
  private friendlyError(err: unknown, fallbackKey: string): Error {
    const body = (err as { error?: unknown } | null)?.error;
    let apiMessage = '';
    if (body && typeof body === 'object') {
      const raw = (body as Record<string, unknown>)['message'] ?? (body as Record<string, unknown>)['Message'];
      apiMessage = typeof raw === 'string' ? raw.trim() : '';
    }
    return new Error(apiMessage || this.translate.instant(fallbackKey));
  }

  private extensionFromFile(file: File): string {
    const name = file.name || '';
    const dot = name.lastIndexOf('.');
    if (dot >= 0) {
      const ext = name.slice(dot);
      return ext.startsWith('.') ? ext : `.${ext}`;
    }
    if (file.type === 'application/pdf') return '.pdf';
    if (file.type === 'image/png') return '.png';
    if (file.type === 'image/jpeg') return '.jpg';
    return '.bin';
  }
}
